import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type HandoverLogEntry, canInAnyScope } from '@testflow/shared';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IdLabel, ResultChip } from '../../components/atoms.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { SavedHint } from '../../components/saved-hint.js';
import { SearchEmptyNotice } from '../../components/search-empty-notice.js';
import { SearchSelect } from '../../components/search-select.js';
import { SidePanel } from '../../components/side-panel.js';
import { catalogApi } from '../../lib/catalog-api.js';
import { handoverApi } from '../../lib/handover-api.js';
import { pickLang } from '../../lib/pick-lang.js';
import {
  type ApplySuggestionsResult,
  type PlanProgressItem,
  type ProjectMember,
  type Suggestion,
  type TestPlanDetail,
  plansApi,
} from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { structureApi } from '../../lib/structure-api.js';
import { toast } from '../../lib/toast.js';
import { useRowSelection } from '../../lib/use-row-selection.js';
import { useSavedFlash } from '../../lib/use-saved-flash.js';
import { useShowSourceRef } from '../../lib/use-source-ref.js';
import { useUrlState } from '../../lib/use-url-state.js';
import { BulkActionBar } from './bulk-action-bar.js';
import { RunHistory } from './run-history.js';

// Design-Spec §3.6 (Screen 3b): Scope-&-Zuweisungs-Tabelle. Gruppenzeilen (fett, "N Testfälle · M
// im Scope") mit eingerückten Testfällen darunter. Spalten: Scope-Checkbox · Name · Tester-Zelle ·
// Fortschritt.
//
// Tester-Zelle: hat das Item einen KI-Vorschlag (plansApi.suggestions) UND ist noch unzugewiesen
// UND wurde der Vorschlag nicht in dieser Session per ✕ verworfen → AiChip "✦ Vorschlag: <Name>"
// mit ✓ (übernimmt via plansApi.setAssignment) / ✕ (verwirft nur lokal, HITL — Design-Spec §5.2:
// nie stille Übernahme). Sonst: <select> mit allen Projekt-Mitgliedern (zeigt zugewiesenen Namen
// als ausgewählte Option, dient zugleich als Umzuweisungs-Kontrolle).
//
// Scope-Checkbox: ruft bei jedem Toggle plansApi.setScope mit der VOLLSTÄNDIGEN aktuellen
// In-Scope-Menge auf (einfachste korrekte Variante — kein Debounce/Apply-Button nötig, der
// Endpoint ist idempotent und ersetzt den gesamten Scope ohnehin bei jedem Aufruf).
//
// #84: 243 Zeilen, 205 Checkboxen, 0 Textfelder — nachgemessen bei 1280×660 ergab das 16
// Bildschirme Scrollstrecke ohne jede Möglichkeit, gezielt zu suchen. Suche (human_id + title_de +
// source_ref, matchesQuery aus Task 3) + zwei Ja/Nein-Filter ("nur im Scope", "nur ohne Tester" —
// die zweite Frage, die man beim Zuweisen ohnehin stellt) laufen über die bereits geladenen Daten,
// kein neuer Endpunkt. Zustand lebt in der URL (useUrlState, Muster #36/#78) — reload- und
// navigationsfest. Suche/Filter gelten für ALLE Rollen (Lese-Operationen); nur die Checkbox
// selbst bleibt an `plan.manage` gebunden. Eine Gruppe, deren Mitglieder alle herausgefiltert
// sind, verschwindet komplett mit — eine leere Gruppenüberschrift wäre irreführend. #78: keine
// stille Kappung — die Zahl der ausgeblendeten Zeilen steht daneben, sobald Filter/Suche aktiv
// sind.
//
// Die zweite, teurere Hälfte des Befunds: Der Scope-Haken speicherte schon immer SOFORT
// (setScope.mutate, siehe oben) — aber ganz ohne sichtbares Feedback, während oben auf derselben
// Seite ein prominenter Speichern-Knopf für den Plan-KOPF steht (plan-detail.tsx). Zwei
// Speicher-Semantiken, eine davon unsichtbar — der Schluss "ich muss hochscrollen" war
// folgerichtig. Fix hier: ein ruhiges, ZEILENNAHES Feedback direkt neben dem Häkchen (kein Toast
// pro Klick — bei 205 Checkboxen wäre das ein Toast-Schwall, siehe Task-5-Bericht). Der Kopf-Knopf
// wurde in plan-detail.tsx umbenannt ("Plandaten speichern"), damit er nicht mehr für den Scope
// gehalten werden kann.
//
// #112 (A4/A5, Task T5): Die bisherige Gliederung kannte nur "E2E-Prozesse" und "Ohne Prozess" —
// ein Testfall, der an einem Prozess UNTER einem Teilprojekt hängt, war über diesen Weg gar nicht
// erreichbar. Ersetzt durch DREI REITER (nicht Abschnitte untereinander: der Nutzer hat die
// ausdrücklich abgelehnt, "dann muss ich wieder ewig scrollen"):
//   „Nach Teilprojekt" (Teilprojekt > Prozess > Testfall) · „Nach E2E-Kette" (Stream > Kette >
//   Testfall, E2E-ID vor dem Namen) · „Nicht zugeordnet · N" (Testfälle ohne Prozess).
// Der aktive Reiter liegt in der URL (`scope_view`) — deep-linkbar und reload-fest wie Suche und
// Filter. Gruppen sind standardmäßig ZUGEKLAPPT: beim Öffnen stehen rund 10 Gruppenzeilen da statt
// 243 Einzelzeilen. Jede Gruppenzeile trägt Stückzahl UND Scope-Zähler und eine dreistufige
// Checkbox (alle/keine/teilweise), damit man eine Gruppe anhaken kann, OHNE sie aufzuklappen.
//
// Derselbe Testfall erscheint bewusst in BEIDEN Sichten (einmal über seinen Prozess, einmal über
// seine Kette) — es sind zwei Sichten auf dieselbe Menge, kein doppelter Testfall. Der Haken ist
// deshalb in beiden Sichten derselbe Zustand, und die Kopfzeile nennt die Zahl EINDEUTIGER
// Testfälle im Scope, damit die Doppelanzeige nicht als doppelte Zählung missverstanden wird.
//
// A5 („Kettenmitgliedschaft gewinnt"): Ein Testfall in einer Kette wird im Kettenkontext getestet
// (Sperre + Ergebnispflicht nach #113), einer ohne Kette ist ein Einzeltest. Beim Anhaken muss
// sichtbar sein, welche Regeln der Fall mitbringt — deshalb trägt er außerhalb der E2E-Sicht ein
// Abzeichen im KLARTEXT ("wird in Kette ETO-E2E-001 getestet"). In der E2E-Sicht wäre es
// redundant: dort IST die Kette die Gruppe.
//
// #114 (A9): Ein ABGESCHLOSSENER Plan ist „lesbar, aber nicht bearbeitbar". Der Server hält das
// bereits ein (assert-plan-open.ts → 409 `plan_closed` auf Scope, Zuweisung, Sammelaktion), diese
// Tabelle bot ihre schreibenden Bedienelemente aber weiter an: Der Nutzer hakte an und lief erst
// beim Klick in den Fehler — ein Bedienelement ohne Wirkung, genau die Fehlerklasse aus
// CLAUDE.md („Vollständigkeit"). Deshalb `readOnly` (= Plan zu) parallel zu `canManage`
// (= Recht fehlt): ZWEI unabhängige Gründe, aus denen hier nichts geht, und der sichtbare Hinweis
// nennt nur den zutreffenden. Der Server bleibt die Autorität — dies ist die Vorwarnung, nicht
// die Prüfung. Lesen, Suchen, Filtern und Auf-/Zuklappen bleiben unangetastet: der Plan soll
// nachlesbar bleiben.
//
// Die Gruppierungsdaten kommen ohne neuen Endpunkt zusammen: Prozess→Teilprojekt aus
// `catalogApi.listProcesses` (`labels` = node_ids), Kette→Stream aus `catalogApi.listE2e`
// (`node_id`, seit T3), die Knotennamen/-arten aus `structureApi.list`. Alle vier Abfragen laufen
// ohnehin schon im Projekt und teilen sich ihre queryKeys mit Katalog und Adminbereich.
interface DisplayRow {
  test_case_id: string;
  human_id: string;
  // #112 (A6): die Quell-ID aus der Importdatei — steht neben der TestFlow-ID und zaehlt in der
  // Suche mit. Beide Zeilenquellen liefern sie: die Testfallliste (catalogApi.listTestCases) und
  // die Kettenmitglieder (E2eMemberView).
  source_ref: string | null;
  title_de: string;
  // #179: Anzeige in der Oberflaechensprache (pickLang), Rueckfall auf Deutsch.
  title_en: string;
}

// I6 (Review-Finding): TesterCell/StatusCell/Row standen bisher INNERHALB von ScopeTable — bei
// jedem Render entstanden dadurch neue Komponenten-Identitaeten, React unmountete/remountete die
// kompletten Zeilen-Subtrees (243 Zeilen). Bisher selten sichtbar; seit #84 loest JEDES Zeichen
// im Suchfeld ein setSearchParams (also einen Render) aus — auf genau dem Screen, dessen Groesse
// der Anlass fuer #84 war. Fix: alle drei auf Modulebene, Daten kommen ausschliesslich ueber
// Props statt ueber Closures aus ScopeTable.

// #195: Die Hinweiszeile „umverteilt von …" unter dem Tester. Die API liefert nur noch wirksame
// Weitergaben, gezeigt wird sie hier trotzdem nur, solange ihr Ziel der angezeigte Tester ist: nach
// einer Umzuweisung ist die Query bis zum Neuladen kurz veraltet, und ein Hinweis auf einen
// Tester, der es nicht mehr ist, wäre falsch.
function TesterCell(props: TesterControlProps & { handover?: HandoverLogEntry | undefined }) {
  const { handover, ...control } = props;
  const { t, i18n } = useTranslation();
  const hint = handover && handover.to_user_id === props.assignedId ? handover : null;
  if (!hint) return <TesterControl {...control} />;
  const at = new Date(hint.at);
  const lang = i18n.resolvedLanguage;
  const date = at.toLocaleDateString(lang, { day: '2-digit', month: '2-digit' });
  const datetime = at.toLocaleString(lang, { dateStyle: 'short', timeStyle: 'short' });
  return (
    <div className="flex flex-col gap-1">
      <TesterControl {...control} />
      <span
        className="text-[11px] text-text-muted"
        title={t(hint.mode === 'give' ? 'plans.handoverHintGive' : 'plans.handoverHintTake', {
          datetime,
        })}
      >
        {t('plans.handoverHint', { name: hint.from_name, date })}
      </span>
    </div>
  );
}

interface TesterControlProps {
  row: DisplayRow;
  canManage: boolean;
  /** #114 (A9): Plan abgeschlossen — die Zelle bleibt sichtbar, gibt aber nichts mehr her. */
  readOnly: boolean;
  assignedId: string | null;
  suggestion: Suggestion | undefined;
  dismissed: Set<string>;
  memberById: Map<string, ProjectMember>;
  members: ProjectMember[];
  onAcceptSuggestion: (testerId: string) => void;
  onRejectSuggestion: () => void;
  onReassign: (testerId: string | null) => void;
}

function TesterControl({
  row,
  canManage,
  readOnly,
  assignedId,
  suggestion,
  dismissed,
  memberById,
  members,
  onAcceptSuggestion,
  onRejectSuggestion,
  onReassign,
}: TesterControlProps) {
  const { t, i18n } = useTranslation();
  const title = pickLang(i18n.resolvedLanguage, row.title_de, row.title_en);
  // Ohne plan.manage: nur den zugewiesenen Tester (oder „kein Tester") als Text zeigen — kein
  // ✦-Vorschlag mit Übernehmen/Verwerfen (setAssignment/dismiss) und keine Umzuweisungs-Auswahl.
  if (!canManage) {
    const name = assignedId
      ? (memberById.get(assignedId)?.name ?? assignedId)
      : t('plans.noTester');
    return <span className="text-xs text-text-body">{name}</span>;
  }
  // #114 (A9): Am abgeschlossenen Plan kein ✦-Vorschlag mit ✓/✕ — die Übernahme IST eine
  // Zuweisung und liefe in 409 `plan_closed`. Die Zelle fällt auf die (deaktivierte) Auswahl
  // zurück, die den zugewiesenen Tester weiterhin zeigt.
  const showSuggestion = !readOnly && !assignedId && suggestion && !dismissed.has(row.test_case_id);

  if (showSuggestion) {
    const suggestedName =
      memberById.get(suggestion.suggested_tester_id)?.name ?? suggestion.suggested_tester_id;
    return (
      <span className="flex items-center gap-2">
        <span className="rounded-chip border border-dashed border-[var(--border)] bg-[var(--tint-blue)] px-2 py-1 text-[11px] text-text-body">
          ✦ {t('plans.suggest')}: {suggestedName}
        </span>
        <button
          type="button"
          aria-label={t('plans.acceptSuggestion', { title })}
          className="text-status-pass"
          onClick={() => onAcceptSuggestion(suggestion.suggested_tester_id)}
        >
          ✓
        </button>
        <button
          type="button"
          aria-label={t('plans.rejectSuggestion', { title })}
          className="text-status-fail"
          onClick={onRejectSuggestion}
        >
          ✕
        </button>
      </span>
    );
  }

  return (
    <SearchSelect
      label={t('plans.testerFor', { title })}
      value={assignedId ?? ''}
      disabled={readOnly}
      onChange={(v) => onReassign(v === '' ? null : v)}
      options={members.map((m) => ({ value: m.user_id, label: m.name }))}
      emptyLabel={t('plans.noTester')}
      className="w-40 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-primary disabled:opacity-60"
    />
  );
}

// #35: Ergebnis-Chip + Laufanzahl, Klick öffnet die Lauf-Historie.
function StatusCell({
  row,
  entry,
  onOpenRun,
}: {
  row: DisplayRow;
  entry: PlanProgressItem | undefined;
  onOpenRun: () => void;
}) {
  const { t, i18n } = useTranslation();
  const title = pickLang(i18n.resolvedLanguage, row.title_de, row.title_en);
  const count = entry?.runs_count ?? 0;
  // Ohne Lauf gibt es nichts aufzuklappen — dann kein Button, sonst führt ein Klick ins Leere.
  if (count === 0) return <ResultChip status="open" />;
  return (
    <button
      type="button"
      aria-label={`${t('plans.runHistory')}: ${title}`}
      onClick={onOpenRun}
      className="rounded-btn px-1 hover:bg-[var(--tint-blue)]"
    >
      <ResultChip status={entry?.status ?? 'open'} count={count} />
    </button>
  );
}

// #112 (A4): Dreistufige Gruppen-Checkbox (alle / keine / teilweise). `indeterminate` ist KEINE
// Markup-Eigenschaft, sondern nur an der DOM-Instanz setzbar (ref-Callback). Als eigener kleiner
// Baustein statt vier Kopien desselben Callbacks: Teilprojekt-/Stream-, Prozess-/Ketten-, Auswahl-
// und Kopf-Ebene brauchen exakt dasselbe Verhalten — vier Kopien wären vier Gelegenheiten, es
// unterschiedlich zu machen.
function TriCheckbox({
  label,
  checked,
  indeterminate,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  /** „teilweise": mindestens eine, aber nicht jede Zeile der Gruppe. */
  indeterminate: boolean;
  disabled?: boolean;
  onChange: () => void;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      disabled={disabled}
      // Nur solange die Gruppe NICHT ohnehin komplett angehakt ist — sonst überlagert der dritte
      // Zustand optisch den eindeutigen zweiten.
      ref={(el) => {
        if (el) el.indeterminate = indeterminate && !checked;
      }}
      onChange={onChange}
    />
  );
}

function Row({
  row,
  inScope,
  canManage,
  readOnly,
  saved,
  onToggleScope,
  assignedId,
  suggestion,
  dismissed,
  memberById,
  members,
  onAcceptSuggestion,
  onRejectSuggestion,
  onReassign,
  progressEntry,
  onOpenRun,
  selectable,
  rowSelected,
  onToggleSelect,
  showSourceRef,
  chainBadge,
  handover,
}: {
  row: DisplayRow;
  inScope: boolean;
  canManage: boolean;
  /** #114 (A9): Plan abgeschlossen — Scope-Haken und Tester-Zelle bleiben sichtbar, aber gesperrt. */
  readOnly: boolean;
  saved: boolean;
  onToggleScope: () => void;
  assignedId: string | null;
  suggestion: Suggestion | undefined;
  dismissed: Set<string>;
  memberById: Map<string, ProjectMember>;
  members: ProjectMember[];
  onAcceptSuggestion: (testerId: string) => void;
  onRejectSuggestion: () => void;
  onReassign: (testerId: string | null) => void;
  progressEntry: PlanProgressItem | undefined;
  onOpenRun: () => void;
  // #91: eigene Checkbox-Spalte fuer die Mehrfachauswahl der Aktionsleiste — NUR bei plan.manage
  // (= canManage), dieselbe Gatung wie die bestehende Scope-Checkbox (#58).
  selectable: boolean;
  rowSelected: boolean;
  onToggleSelect: () => void;
  /** #112 (A6): Admin-Schalter `show_source_ref` — aus, verschwindet die Quell-ID. */
  showSourceRef: boolean;
  /**
   * #112 (A5): Kettenmitgliedschaft im Klartext („ETO-E2E-001"), sonst `null`. Kein stilles
   * Symbol: beim Anhaken muss ohne Vorwissen erkennbar sein, dass dieser Testfall im
   * Kettenkontext getestet wird (Sperre + Ergebnispflicht nach #113) und nicht als Einzeltest.
   */
  chainBadge: string | null;
  /** #195: wirksame letzte Weitergabe dieses Testfalls („umverteilt von …"), sonst undefined. */
  handover: HandoverLogEntry | undefined;
}) {
  const { t, i18n } = useTranslation();
  const title = pickLang(i18n.resolvedLanguage, row.title_de, row.title_en);
  const tint =
    !assignedId && suggestion && !dismissed.has(row.test_case_id)
      ? 'bg-[var(--tint-yellow)]'
      : undefined;
  return (
    <tr className={tint}>
      {selectable && (
        <td className="px-4 py-2">
          <input
            type="checkbox"
            aria-label={t('plans.bulk.selectRow', { title })}
            checked={rowSelected}
            onChange={onToggleSelect}
          />
        </td>
      )}
      <td className="px-4 py-2">
        <span className="flex items-center gap-2">
          <input
            type="checkbox"
            aria-label={t('plans.scopeFor', { title })}
            checked={inScope}
            // Ohne plan.manage read-only: der Scope-Zustand bleibt sichtbar, ist aber nicht
            // umschaltbar (kein setScope). #114 (A9): am abgeschlossenen Plan gilt dasselbe —
            // zweiter, unabhängiger Grund (Server: 409 `plan_closed`).
            disabled={!canManage || readOnly}
            onChange={onToggleScope}
          />
          {/* #84/#96: ruhiges, zeilennahes Erfolgs-Feedback statt Toast — deckt sowohl den
              Scope-Haken als auch die Testerzuweisung ab (beide flashen dieselbe Zeile). */}
          <SavedHint visible={saved} />
        </span>
      </td>
      <td className="px-4 py-2 pl-12 text-text-body">
        <IdLabel id={row.human_id} secondary={showSourceRef ? row.source_ref : null} /> {title}
        {chainBadge && (
          <span className="ml-2 whitespace-nowrap rounded-chip border border-[var(--border)] bg-[var(--tint-blue)] px-2 py-0.5 text-[11px] text-text-body">
            {t('plans.chainBadge', { chain: chainBadge })}
          </span>
        )}
      </td>
      <td className="px-4 py-2">
        <TesterCell
          row={row}
          canManage={canManage}
          readOnly={readOnly}
          assignedId={assignedId}
          suggestion={suggestion}
          dismissed={dismissed}
          memberById={memberById}
          members={members}
          onAcceptSuggestion={onAcceptSuggestion}
          onRejectSuggestion={onRejectSuggestion}
          onReassign={onReassign}
          handover={handover}
        />
      </td>
      <td className="px-4 py-2">
        <StatusCell row={row} entry={progressEntry} onOpenRun={onOpenRun} />
      </td>
    </tr>
  );
}

/** Die drei Reiter aus A4. Der Wert steht so auch in der URL (`scope_view`). */
type ScopeView = 'teilprojekt' | 'e2e' | 'unassigned';

/** Zweite Gruppenebene: Prozess (Teilprojekt-Sicht) bzw. Kette (E2E-Sicht). */
interface LeafGroup {
  key: string;
  name: string;
  /** Nur Ketten: die E2E-ID steht laut A4 VOR dem Namen (`ETO-E2E-001 Order to Cash`). */
  humanId: string | null;
  rows: DisplayRow[];
}
/** Erste Gruppenebene: Teilprojekt bzw. Stream — inklusive der jeweiligen Restmenge (A2). */
interface TopGroup {
  key: string;
  name: string;
  children: LeafGroup[];
}

export function ScopeTable({ projectId, planId }: { projectId: string; planId: string }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  // #58: Scope setzen, Tester zuweisen und Vorschläge übernehmen sind Schreibaktionen am Testplan
  // (`plan.manage`, Backend erzwingt es zusätzlich). Ohne das Recht bleibt die Tabelle rein
  // lesbar: Scope-Häkchen als Zustand ohne Umschaltung, Tester als Text, keine ✦-Aktionen.
  const canManage = user ? canInAnyScope(user, 'plan.manage') : false;
  // #112 (A6): derselbe Admin-Schalter wie im Katalog — die Quell-ID soll an beiden Orten
  // gleichzeitig da sein oder gleichzeitig weg, sonst wirkt sie an einem Ort verloren.
  const showSourceRef = useShowSourceRef(projectId);
  // Lokal verworfene Vorschläge (✕) — nur für diese Session, keine Server-Mutation (HITL: ein
  // Verwerfen ist kein Zustand, den man dauerhaft speichert, der Vorschlag kann jederzeit
  // wiederkehren, sobald die Seite neu geladen wird).
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  // #84: Such-/Filterzustand lebt in der URL — deep-linkbar, reload- und navigationsfest (Muster
  // #36/#78). "Nur im Scope" und "nur ohne Tester" sind zwei unabhängige Ja/Nein-Filter (UND
  // verknüpft), keine Radiogruppe — beide Fragen können gleichzeitig gelten.
  // Eigener Schlüssel, NICHT `q`: den belegt bereits die Katalog-Suche (#78, catalog-page.tsx).
  // Heute rendern beide Seiten nie gleichzeitig, aber ein geteilter Schlüssel heißt, dass ein
  // Link nicht mehr sagen kann, welche der beiden Suchen er meint — und beim ersten gemeinsamen
  // Screen überschreiben sie sich gegenseitig.
  const [qParam, setQParam] = useUrlState('scope_q', '');
  const trimmedQuery = qParam.trim();
  const [onlyScopeParam, setOnlyScopeParam] = useUrlState('onlyScope', 'false');
  const onlyScope = onlyScopeParam === 'true';
  const [onlyUnassignedParam, setOnlyUnassignedParam] = useUrlState('onlyUnassigned', 'false');
  const onlyUnassigned = onlyUnassignedParam === 'true';
  // #112 (A4): der aktive Reiter gehört in dieselbe URL wie Suche und Filter — sonst überlebt er
  // keinen Reload, und ein geteilter Link zeigt nicht dieselbe Sicht. Unbekannte Werte (händisch
  // editierte URL) fallen auf die Vorgabe zurück, statt eine leere Tabelle zu zeigen.
  const [viewParam, setViewParam] = useUrlState('scope_view', 'teilprojekt');
  const view: ScopeView =
    viewParam === 'e2e' || viewParam === 'unassigned' ? viewParam : 'teilprojekt';
  // #91: Mehrfachauswahl fuer die Aktionsleiste — fluechtiger lokaler State (nie URL). Der
  // resetKey traegt den Filter-Fingerabdruck: aendert sich Suche/Filter/Reiter, leert sich die
  // Auswahl, damit eine Sammelaktion nie unsichtbare Zeilen trifft. Der Reiter gehoert dazu, weil
  // er dieselbe Wirkung hat wie ein Filter: er blendet einen Teil der Zeilen aus.
  const selection = useRowSelection(`${view}|${trimmedQuery}|${onlyScope}|${onlyUnassigned}`);
  // #84/#96: ruhiges, zeilennahes Erfolgs-Feedback statt Toast (Begründung siehe Kopf-Kommentar) —
  // `savedId` ist die zuletzt erfolgreich gespeicherte Zeile, für ein paar Sekunden sichtbar.
  // Task 9: aus scope-table extrahiert (use-saved-flash.ts), damit BEIDE Zeilen-Mutationen
  // (Scope-Haken UND Testerzuweisung) dasselbe Feedback teilen.
  const { savedId, flash } = useSavedFlash();

  // #112 (A4): Gruppen sind standardmäßig ZUGEKLAPPT — beim Öffnen sollen rund 10 Gruppenzeilen
  // dastehen, nicht 243 Einzelzeilen. Die Map hält nur die Gruppen, die der Nutzer selbst
  // umgeschaltet hat; alles andere folgt der Regel in `isOpen`. Bewusst KEIN „collapsed"-Set
  // mehr: mit aktiver Suche ist die Vorgabe „offen" (Treffer sollen sichtbar sein), ohne Suche
  // „zu" — ein einzelnes Set könnte diese zwei Vorgaben nicht auseinanderhalten.
  const [openOverride, setOpenOverride] = useState<Map<string, boolean>>(() => new Map());

  // I5 (Review-Finding, Lost Update): mehrfach gebraucht — von der Query, von `invalidate` UND
  // vom optimistischen Update in `setScope.onMutate` weiter unten. Eine Konstante statt drei
  // Literale, die auseinanderlaufen koennten.
  const planQueryKey = ['plan', projectId, planId] as const;

  const planQuery = useQuery({
    queryKey: planQueryKey,
    queryFn: () => plansApi.get(projectId, planId),
    enabled: Boolean(projectId) && Boolean(planId),
  });
  const { data: plan } = planQuery;
  const testCasesQuery = useQuery({
    queryKey: ['catalog-testcases', projectId],
    queryFn: () => catalogApi.listTestCases(projectId),
    enabled: Boolean(projectId),
  });
  const { data: testCases } = testCasesQuery;
  // Prozesse: Name für die zweite Gruppenebene der Teilprojekt-Sicht UND `labels` (node_ids) für
  // die Zuordnung Prozess → Teilprojekt. process_id steckt bereits in den geladenen Testfällen.
  const processesQuery = useQuery({
    queryKey: ['catalog-processes', projectId],
    queryFn: () => catalogApi.listProcesses(projectId),
    enabled: Boolean(projectId),
  });
  const { data: processes } = processesQuery;
  const e2eListQuery = useQuery({
    queryKey: ['catalog-e2e', projectId],
    queryFn: () => catalogApi.listE2e(projectId),
    enabled: Boolean(projectId),
  });
  const { data: e2eList } = e2eListQuery;
  // #112 (A4): Teilprojekte und Streams — die erste Gruppenebene beider Sichten. Derselbe
  // queryKey wie im Admin-Panel und auf der Zuordnungsseite: wird ein Knoten dort umbenannt,
  // zieht diese Tabelle ohne eigenes Zutun nach.
  const nodesQuery = useQuery({
    queryKey: ['structure-nodes', projectId],
    queryFn: () => structureApi.list(projectId),
    enabled: Boolean(projectId),
  });
  const { data: nodes } = nodesQuery;
  const { data: members } = useQuery({
    queryKey: ['plan-members', projectId],
    queryFn: () => plansApi.members(projectId),
    enabled: Boolean(projectId),
  });
  const { data: suggestions } = useQuery({
    queryKey: ['plan-suggestions', projectId, planId],
    queryFn: () => plansApi.suggestions(projectId, planId),
    enabled: Boolean(projectId) && Boolean(planId),
  });
  // #35: Ausführungsstatus je Testfall. Bewusst dieselbe queryKey wie im Fortschritts-Abschnitt,
  // damit beide Ansichten sich einen Fetch teilen statt zweimal zu laden.
  const { data: progress } = useQuery({
    queryKey: ['plan-progress', projectId, planId],
    queryFn: () => plansApi.progress(projectId, planId),
    enabled: Boolean(projectId) && Boolean(planId),
  });
  // #195: wirksame Weitergaben (Audit `plan.handover`) für die Hinweiszeile „umverteilt von …".
  // Lesen darf jeder mit `report.read`; schlägt die Abfrage fehl, fehlt nur der Hinweis — die
  // Tabelle selbst bleibt benutzbar (kein Eintrag in der Fehlerliste unten).
  const { data: handovers } = useQuery({
    queryKey: ['plan-handovers', projectId, planId],
    queryFn: () => handoverApi.log(projectId, planId),
    enabled: Boolean(projectId) && Boolean(planId),
  });
  const handoverByTc = useMemo(
    () => new Map((handovers ?? []).map((h) => [h.test_case_id, h])),
    [handovers],
  );
  // Offener Drill-down: Testfall-ID + Titel für den Panel-Kopf.
  const [openRun, setOpenRun] = useState<{ id: string; title: string } | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: planQueryKey });
    void queryClient.invalidateQueries({ queryKey: ['plan-suggestions', projectId, planId] });
    // #195: Jede Umzuweisung (Einzel, Sammel, Vorschlag) macht einen „umverteilt von …"-Hinweis
    // unwirksam — die API rechnet das beim Lesen, also neu lesen.
    void queryClient.invalidateQueries({ queryKey: ['plan-handovers', projectId, planId] });
  };

  const setScope = useMutation({
    mutationFn: (ids: string[]) => plansApi.setScope(projectId, planId, ids),
    // I5 (Review-Finding, Lost Update): `toggleScope` baute die naechste Menge bisher AUS DEM
    // CACHE (`inScopeIds`, abgeleitet aus `plan.items`) — ohne optimistisches Update sah ein
    // zweiter Klick VOR dem Refetch des ersten noch den alten Stand und ueberschrieb ihn beim
    // Speichern (setScope ersetzt serverseitig den GESAMTEN Scope). #84 macht "zuegig eine
    // gefilterte Liste durchhaken" zum Normalfall — genau das Bedienmuster, das den Fehler
    // zuverlaessig ausloest. cancelQueries verhindert zusaetzlich, dass ein noch laufender
    // Background-Refetch den optimistischen Stand gleich wieder ueberschreibt.
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey: planQueryKey });
      const previous = queryClient.getQueryData<TestPlanDetail>(planQueryKey);
      if (previous) {
        const nextScope = new Set(ids);
        const existingIds = new Set(previous.items.map((i) => i.test_case_id));
        const now = new Date().toISOString();
        const items = [
          // Bestehende Items: in_scope aus der neuen Menge uebernehmen, sonst unveraendert
          // (insb. assigned_tester_id bleibt stehen — setScope ruehrt die Zuweisung nicht an).
          ...previous.items.map((item) => ({
            ...item,
            in_scope: nextScope.has(item.test_case_id),
          })),
          // Neu in den Scope aufgenommene Testfaelle, die noch KEIN Item hatten (erste
          // Scope-Setzung ueberhaupt fuer diesen Testfall in diesem Plan) — sonst fehlten sie in
          // `inScopeIds` und ein dritter, noch schnellerer Klick wuerde sie wieder verlieren.
          ...ids
            .filter((id) => !existingIds.has(id))
            .map((id) => ({
              item_id: `optimistic-${id}`,
              test_plan_id: planId,
              test_case_id: id,
              in_scope: true,
              assigned_tester_id: null,
              created_at: now,
              updated_at: now,
            })),
        ];
        queryClient.setQueryData<TestPlanDetail>(planQueryKey, { ...previous, items });
      }
      return { previous };
    },
    // Schlaegt der Request fehl, muss der optimistische Stand weichen — sonst zeigt die Tabelle
    // dauerhaft einen Scope, den der Server nie gespeichert hat. Der globale Fehler-Toast
    // (query-client.ts) informiert ohnehin schon.
    onError: (_err, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(planQueryKey, context.previous);
    },
    onSuccess: invalidate,
  });
  const setAssignment = useMutation({
    mutationFn: (body: { test_case_id: string; tester_id: string | null }) =>
      plansApi.setAssignment(projectId, planId, body),
    onSuccess: invalidate,
  });
  // Rückmeldung nach „Vorschläge übernehmen": die Übersprungenen (Platzhalter ohne Projektrolle,
  // deaktivierte Nutzer) bleiben als aufklappbarer Hinweis stehen — ein Toast verschwindet, bevor
  // man die Liste gelesen hat. Der nächste Lauf ersetzt den Hinweis, er ist schließbar.
  const [applyResult, setApplyResult] = useState<ApplySuggestionsResult | null>(null);
  const applyAll = useMutation({
    mutationFn: () => plansApi.applySuggestions(projectId, planId),
    onSuccess: (result) => {
      invalidate();
      // Bei 0 übernommenen Einträgen wäre der Toast nur Rauschen — der Hinweis-Block unten nennt
      // die Übersprungenen.
      if (result.applied > 0) {
        toast.success(t('plans.applyResult.applied', { count: result.applied }));
      }
      setApplyResult(result.skipped.length > 0 ? result : null);
    },
  });
  // #91: Sammelzuweisung/-entfernung ueber die Aktionsleiste — EIN Request statt N
  // Einzel-PATCHes. Schlaegt der GESAMTE Request fehl (z. B. HTTP 422 bei ungueltigem Tester),
  // reicht der globale Fehler-Toast (query-client.ts); die Auswahl bleibt fuer den Retry stehen
  // (kein `selection.clear()` im Fehlerfall).
  const bulkAssign = useMutation({
    mutationFn: (body: { test_case_ids: string[]; tester_id: string | null }) =>
      plansApi.bulkAssign(projectId, planId, body),
    onSuccess: (result, body) => {
      invalidate();
      selection.clear();
      announceBulkResult(result, body.tester_id ? memberById.get(body.tester_id)?.name : null);
    },
  });

  // #89: Ein Fehler in EINER der Pflicht-Queries fiel bisher durch `!plan`/`!testCases`/
  // `!e2eList` in denselben Guard wie das normale Laden — der Nutzer sah ein endloses "lädt…"
  // statt eines Hinweises, dass etwas schiefgegangen ist (Kernsatz: Query-Fehler darf NIE als
  // Endlos-Laden erscheinen). VOR dem Lade-Guard geprüft, Retry stößt gezielt nur die
  // tatsächlich gescheiterten Queries neu an. #112: Prozesse und Struktur-Knoten stehen jetzt mit
  // in der Liste — beide sind seit den Reitern Pflichtdaten (sie tragen die Gruppenebenen) und
  // standen bisher nur im Lade-Guard, ein Fehler dort ergab also genau das Endlos-"lädt…".
  if (
    planQuery.isError ||
    testCasesQuery.isError ||
    e2eListQuery.isError ||
    processesQuery.isError ||
    nodesQuery.isError
  ) {
    return (
      <QueryErrorNotice
        onRetry={() => {
          if (planQuery.isError) void planQuery.refetch();
          if (testCasesQuery.isError) void testCasesQuery.refetch();
          if (e2eListQuery.isError) void e2eListQuery.refetch();
          if (processesQuery.isError) void processesQuery.refetch();
          if (nodesQuery.isError) void nodesQuery.refetch();
        }}
      />
    );
  }

  if (!plan || !testCases || !e2eList || !processes || !nodes) {
    return <div className="text-text-muted">{t('common.loading')}</div>;
  }

  // #114 (A9): der zweite Sperrgrund neben `canManage` (Begründung im Datei-Kopf). Bewusst aus
  // der ohnehin geladenen Plan-Abfrage abgeleitet statt als Prop von plan-detail.tsx: beide
  // teilen sich denselben queryKey, ein Statuswechsel („Wieder öffnen") entsperrt die Tabelle
  // damit ohne Zutun — und die Herkunft der Aussage bleibt EINE (der Server).
  const readOnly = plan.status === 'abgeschlossen';

  const items = plan.items;
  const inScopeIds = new Set(items.filter((i) => i.in_scope).map((i) => i.test_case_id));
  const assignedByTc = new Map(items.map((i) => [i.test_case_id, i.assigned_tester_id]));
  const memberById = new Map((members ?? []).map((m) => [m.user_id, m]));
  const suggestionByTc = new Map((suggestions ?? []).map((s) => [s.test_case_id, s]));
  const suggestionCount = suggestions?.length ?? 0;
  const progressByTc = new Map((progress?.items ?? []).map((p) => [p.test_case_id, p]));

  // #84: EINE Sichtbarkeitsregel für Suche + beide Filter, auf jede Zeile gleich angewendet.
  const rowVisible = (row: DisplayRow): boolean => {
    if (onlyScope && !inScopeIds.has(row.test_case_id)) return false;
    if (onlyUnassigned && assignedByTc.get(row.test_case_id)) return false;
    // #112 (A6): source_ref zaehlt mit — „WMI_" findet hier dieselbe Gruppe wie im Katalog. Die
    // Suche bleibt aktiv, auch wenn der Admin-Schalter die ANZEIGE der Quell-ID abgeschaltet hat.
    return matchesQuery(trimmedQuery, row.human_id, row.title_de, row.title_en, row.source_ref);
  };
  const searching = trimmedQuery !== '';
  const filterActive = searching || onlyScope || onlyUnassigned;

  // --- Gruppenbäume der drei Reiter (A4) -----------------------------------------------------
  // Restmengen-Schlüssel: Prozesse ohne Teilprojekt bzw. Ketten ohne Stream. Die BESCHRIFTUNGEN
  // sind laut A2 verschieden („Ohne Teilprojekt" / „Ohne Stream") — geteilt ist nur der interne
  // Schlüssel.
  const NO_NODE = '__none__';
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);

  // Testfälle je Prozess. Testfälle OHNE Prozess hängen an keinem Ast und bekommen mit dem Reiter
  // „Nicht zugeordnet" ihren eigenen Ort (A2) — sie tauchen deshalb hier nicht auf.
  const rowsByProcess = new Map<string, DisplayRow[]>();
  const unassignedRows: DisplayRow[] = [];
  for (const tc of testCases) {
    if (!tc.process_id) {
      unassignedRows.push(tc);
      continue;
    }
    const list = rowsByProcess.get(tc.process_id);
    if (list) list.push(tc);
    else rowsByProcess.set(tc.process_id, [tc]);
  }

  const teilprojektNodes = nodes.filter((n) => n.kind === 'teilprojekt');
  const streamNodes = nodes.filter((n) => n.kind === 'stream');
  const teilprojektIds = new Set(teilprojektNodes.map((n) => n.node_id));

  // Prozess → Teilprojekt. A1 erlaubt höchstens EIN Teilprojekt je Projekt; `labels` kann
  // trotzdem mehrere node_ids tragen (der Katalog ist kundenweit, die Knoten sind projektweit) —
  // deshalb wird gezielt das erste Label gesucht, das ein Teilprojekt DIESES Projekts ist.
  const processGroupsByNode = new Map<string, LeafGroup[]>();
  for (const p of [...processes].sort((a, b) => a.name_de.localeCompare(b.name_de))) {
    const nodeId = (p.labels ?? []).find((id) => teilprojektIds.has(id)) ?? NO_NODE;
    const leaf: LeafGroup = {
      key: `proc:${p.process_id}`,
      name: pickLang(i18n.resolvedLanguage, p.name_de, p.name_en),
      humanId: null,
      rows: rowsByProcess.get(p.process_id) ?? [],
    };
    const list = processGroupsByNode.get(nodeId);
    if (list) list.push(leaf);
    else processGroupsByNode.set(nodeId, [leaf]);
  }
  const teilprojektGroups: TopGroup[] = [
    ...[...teilprojektNodes].sort(byName).map((n) => ({
      key: `node:${n.node_id}`,
      name: n.name,
      children: processGroupsByNode.get(n.node_id) ?? [],
    })),
    // Restmenge ans Ende — fest, nicht wegklickbar (A2).
    {
      key: `node:${NO_NODE}`,
      name: t('catalog.tree.withoutTeilprojekt'),
      children: processGroupsByNode.get(NO_NODE) ?? [],
    },
  ];

  // Kette → Stream (T3 liefert `node_id` an der Kette). Mitglieder in Sequenz — die Reihenfolge
  // IST die fachliche Aussage der Kette.
  const chainGroupsByNode = new Map<string, LeafGroup[]>();
  for (const e2e of [...e2eList].sort((a, b) => a.human_id.localeCompare(b.human_id))) {
    const nodeId = e2e.node_id ?? NO_NODE;
    const leaf: LeafGroup = {
      key: `e2e:${e2e.e2e_id}`,
      name: pickLang(i18n.resolvedLanguage, e2e.name_de, e2e.name_en),
      humanId: e2e.human_id,
      rows: [...e2e.members].sort((a, b) => a.sequence - b.sequence),
    };
    const list = chainGroupsByNode.get(nodeId);
    if (list) list.push(leaf);
    else chainGroupsByNode.set(nodeId, [leaf]);
  }
  const e2eGroups: TopGroup[] = [
    ...[...streamNodes].sort(byName).map((n) => ({
      key: `stream:${n.node_id}`,
      name: n.name,
      children: chainGroupsByNode.get(n.node_id) ?? [],
    })),
    {
      key: `stream:${NO_NODE}`,
      name: t('catalog.tree.withoutStream'),
      children: chainGroupsByNode.get(NO_NODE) ?? [],
    },
  ];

  // A5: Kettenzugehörigkeit je Testfall im Klartext. `e2e_members` ist n:m — ein Testfall KANN in
  // mehreren Ketten liegen; dann werden alle genannt, statt still eine davon zu wählen.
  const chainsByTc = new Map<string, string[]>();
  for (const e2e of e2eList) {
    for (const m of e2e.members) {
      const list = chainsByTc.get(m.test_case_id);
      if (list) list.push(e2e.human_id);
      else chainsByTc.set(m.test_case_id, [e2e.human_id]);
    }
  }

  const activeGroups = view === 'e2e' ? e2eGroups : teilprojektGroups;
  // Alle Zeilen des AKTIVEN Reiters — Grundlage für „N ausgeblendet", die Kopf-Checkbox und die
  // Leermeldung. In der E2E-Sicht kann ein Testfall in zwei Ketten stecken; gezählt wird deshalb
  // über test_case_id dedupliziert, sonst meldete die Zeile mehr Testfälle, als es gibt.
  const viewRows: DisplayRow[] =
    view === 'unassigned'
      ? unassignedRows
      : activeGroups.flatMap((g) => g.children.flatMap((c) => c.rows));
  const uniqueIds = (rows: DisplayRow[]) => [...new Set(rows.map((r) => r.test_case_id))];
  const visibleIds = uniqueIds(viewRows.filter(rowVisible));
  const visibleRowCount = visibleIds.length;
  const hiddenRowCount = uniqueIds(viewRows).length - visibleRowCount;
  // #91: fuer die Reason-Benennung im Teilerfolgs-Toast — bewusst ueber ALLE Zeilen aller Reiter,
  // damit ein Testfall auch dann benannt werden kann, wenn inzwischen der Reiter gewechselt hat.
  const rowById = new Map(
    [...testCases, ...e2eList.flatMap((e2e) => e2e.members)].map((r) => [r.test_case_id, r]),
  );
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selection.isSelected(id));
  const someVisibleSelected = visibleIds.some((id) => selection.isSelected(id));
  // A4: EINDEUTIGE Testfälle im Scope. Der Plan führt je Testfall genau ein Item — die Zahl ist
  // damit unabhängig davon, in wie vielen Sichten/Ketten der Testfall auftaucht.
  const uniqueInScopeCount = inScopeIds.size;

  // #112 (A4): Vorgabe „zugeklappt", mit einer Ausnahme — sobald Suche/Filter aktiv sind, gilt
  // für Gruppen MIT Treffern „offen", sonst müsste man jeden Treffer erst aufklappen. Ein Klick
  // des Nutzers schlägt beide Vorgaben.
  const isOpen = (groupKey: string, hasHits: boolean) =>
    openOverride.get(groupKey) ?? (filterActive && hasHits);
  const toggleOpen = (groupKey: string, hasHits: boolean) =>
    setOpenOverride((prev) => {
      const next = new Map(prev);
      next.set(groupKey, !(prev.get(groupKey) ?? (filterActive && hasHits)));
      return next;
    });

  // Gruppen-Kennzahlen (Scope + Auswahl) — einheitlich für beide Gruppenebenen. Der Scope-Haken
  // der Gruppe ist „an", wenn ALLE (sichtbaren) Mitglieder im Scope sind, sonst „teilweise";
  // dieselbe Logik für die Auswahl-Checkbox. Bewusst die SICHTBAREN Zeilen: eine Gruppenaktion
  // darf nie Zeilen treffen, die Suche/Filter gerade ausblenden (#91).
  const groupMeta = (groupRows: DisplayRow[]) => {
    const ids = uniqueIds(groupRows);
    return {
      ids,
      allInScope: ids.length > 0 && ids.every((id) => inScopeIds.has(id)),
      someInScope: ids.some((id) => inScopeIds.has(id)),
      allSelected: ids.length > 0 && ids.every((id) => selection.isSelected(id)),
      someSelected: ids.some((id) => selection.isSelected(id)),
    };
  };

  const toggleScope = (testCaseId: string) => {
    const next = new Set(inScopeIds);
    if (next.has(testCaseId)) next.delete(testCaseId);
    else next.add(testCaseId);
    setScope.mutate([...next], {
      // Ruhiges, zeilennahes Feedback statt Toast — siehe Kopf-Kommentar. Läuft NUR bei Erfolg;
      // ein Fehler landet weiterhin im globalen Fehler-Toast (query-client.ts), das reicht dort.
      onSuccess: () => flash(testCaseId),
    });
  };

  // #91: "In Scope"/"Aus Scope" aus der Aktionsleiste nutzen die BESTEHENDE setScope-Vollmenge —
  // kein neuer Endpunkt. Baut auf `inScopeIds` (aus dem Cache, nicht der Auswahl) auf, damit
  // Zeilen ausserhalb der Auswahl unangetastet bleiben.
  const bulkScope = (into: boolean) => {
    const next = new Set(inScopeIds);
    for (const id of selection.selected) {
      if (into) next.add(id);
      else next.delete(id);
    }
    setScope.mutate([...next], {
      onSuccess: () => {
        toast.success(
          t(into ? 'plans.bulk.scoped' : 'plans.bulk.unscoped', { count: selection.selected.size }),
        );
        selection.clear();
      },
    });
  };
  // Ganze Gruppe (Teilprojekt, Prozess, Stream oder Kette) auf einen Schlag in/aus Scope —
  // dieselbe setScope-Vollmengen-Semantik wie der einzelne Haken und bulkScope, nur über eine
  // feste Id-Menge statt der aktuellen Auswahl. Das ist der Weg, der die Gruppen-Checkbox OHNE
  // Aufklappen bedienbar macht (A4).
  const setGroupScope = (ids: string[], into: boolean) => {
    if (ids.length === 0) return;
    const next = new Set(inScopeIds);
    for (const id of ids) {
      if (into) next.add(id);
      else next.delete(id);
    }
    setScope.mutate([...next]);
  };

  // Teilerfolg (Server-Vertrag Task 10/11): `updated + skipped.length` === deduplizierte Eingabe.
  // Uebersprungene werden NAMENTLICH benannt (Testfall-Id + Grund), nicht nur gezaehlt — sonst
  // weiss der Nutzer nicht, WELCHE drei der zwoelf markierten Zeilen die Aktion verfehlt hat.
  function announceBulkResult(
    result: { updated: number; skipped: { test_case_id: string; reason: string }[] },
    name: string | null | undefined,
  ) {
    if (result.skipped.length === 0) {
      toast.success(
        name
          ? t('plans.bulk.assigned', { count: result.updated, name })
          : t('plans.bulk.removed', { count: result.updated }),
      );
      return;
    }
    const details = result.skipped
      .map(
        (s) =>
          `${rowById.get(s.test_case_id)?.human_id ?? s.test_case_id} (${t(`plans.bulk.reason.${s.reason}`)})`,
      )
      .join(', ');
    toast.info(
      t('plans.bulk.partial', {
        done: result.updated,
        total: result.updated + result.skipped.length,
        details,
      }),
    );
  }

  // I6: kleine, stabile Callback-Fabriken statt der frueheren Closures direkt in TesterCell — die
  // Komponenten selbst leben jetzt auf Modulebene (oben), nur diese Funktionen wandern noch als
  // Props durch.
  const acceptSuggestion = (testCaseId: string, testerId: string) => {
    // #96: dasselbe ruhige, zeilennahe Feedback wie beim Scope-Haken — die Übernahme eines
    // Vorschlags ist ebenfalls eine Testerzuweisung.
    setAssignment.mutate(
      { test_case_id: testCaseId, tester_id: testerId },
      { onSuccess: () => flash(testCaseId) },
    );
  };
  const rejectSuggestion = (testCaseId: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(testCaseId);
      return next;
    });
  };
  const reassignTester = (testCaseId: string, testerId: string | null) => {
    setAssignment.mutate(
      { test_case_id: testCaseId, tester_id: testerId },
      { onSuccess: () => flash(testCaseId) },
    );
  };

  // I6: eine Stelle, die aus einer DisplayRow ein <Row>-Element mit allen Props baut — Row selbst
  // lebt auf Modulebene (oben) und bekommt hier nur noch pro-Zeile abgeleitete Werte + stabile
  // Callback-Wrapper gereicht, keine Closures aus dem restlichen Komponentenkoerper direkt.
  //
  // `keyPrefix` ist die Gruppe, unter der die Zeile steht: derselbe Testfall kann in der E2E-Sicht
  // in zwei Ketten auftauchen — ohne Präfix wäre der React-Key doppelt vergeben.
  const renderRow = (row: DisplayRow, keyPrefix: string) => (
    <Row
      key={`${keyPrefix}/${row.test_case_id}`}
      row={row}
      inScope={inScopeIds.has(row.test_case_id)}
      canManage={canManage}
      readOnly={readOnly}
      saved={savedId === row.test_case_id}
      onToggleScope={() => toggleScope(row.test_case_id)}
      assignedId={assignedByTc.get(row.test_case_id) ?? null}
      suggestion={suggestionByTc.get(row.test_case_id)}
      dismissed={dismissed}
      memberById={memberById}
      members={members ?? []}
      onAcceptSuggestion={(testerId) => acceptSuggestion(row.test_case_id, testerId)}
      onRejectSuggestion={() => rejectSuggestion(row.test_case_id)}
      onReassign={(testerId) => reassignTester(row.test_case_id, testerId)}
      progressEntry={progressByTc.get(row.test_case_id)}
      onOpenRun={() =>
        setOpenRun({
          id: row.test_case_id,
          title: pickLang(i18n.resolvedLanguage, row.title_de, row.title_en),
        })
      }
      selectable={canManage}
      rowSelected={selection.isSelected(row.test_case_id)}
      onToggleSelect={() => selection.toggle(row.test_case_id)}
      showSourceRef={showSourceRef}
      handover={handoverByTc.get(row.test_case_id)}
      // A5: in der E2E-Sicht wäre das Abzeichen redundant — dort IST die Kette die Gruppe.
      chainBadge={view === 'e2e' ? null : (chainsByTc.get(row.test_case_id)?.join(', ') ?? null)}
    />
  );

  // Einheitliche Gruppen-Kopfzeile für BEIDE Ebenen (Teilprojekt/Stream und Prozess/Kette):
  // Auswahl-Checkbox, dreistufiger Gruppen-Scope-Haken, Aufklapp-Umschalter, Stückzahl und
  // Scope-Zähler. `rows` sind ALLE Zeilen der Gruppe (die Zähler beschreiben die Gruppe, nicht den
  // Filter — dafür steht die „N ausgeblendet"-Zahl oben), `visibleRows` die aktuell sichtbaren
  // (nur auf sie wirken die Checkboxen, #91).
  const renderGroupHeader = (opts: {
    groupKey: string;
    name: string;
    humanId: string | null;
    level: 0 | 1;
    rows: DisplayRow[];
    visibleRows: DisplayRow[];
  }) => {
    const meta = groupMeta(opts.visibleRows);
    const open = isOpen(opts.groupKey, opts.visibleRows.length > 0);
    const ids = uniqueIds(opts.rows);
    const inScopeCount = ids.filter((id) => inScopeIds.has(id)).length;
    return (
      <tr
        key={opts.groupKey}
        className={
          opts.level === 0
            ? 'bg-[var(--bg-subtle)] font-bold text-text-primary'
            : 'bg-[var(--bg-subtle)] text-text-primary'
        }
      >
        {canManage && (
          <td className="px-4 py-2">
            <TriCheckbox
              label={t('plans.bulk.selectGroup', { name: opts.name })}
              checked={meta.allSelected}
              indeterminate={meta.someSelected}
              onChange={() => selection.setMany(meta.ids, !meta.allSelected)}
            />
          </td>
        )}
        <td className="px-4 py-2">
          <TriCheckbox
            label={t('plans.scopeForGroup', { name: opts.name })}
            checked={meta.allInScope}
            indeterminate={meta.someInScope}
            // Ohne plan.manage read-only — dieselbe Gatung wie der einzelne Scope-Haken (#58),
            // seit #114 (A9) zusätzlich am abgeschlossenen Plan.
            disabled={!canManage || readOnly}
            onChange={() => setGroupScope(meta.ids, !meta.allInScope)}
          />
        </td>
        <td className="px-4 py-2" colSpan={3}>
          <button
            type="button"
            onClick={() => toggleOpen(opts.groupKey, opts.visibleRows.length > 0)}
            aria-expanded={open}
            className={`flex items-center gap-2 text-left ${opts.level === 1 ? 'pl-6' : ''}`}
          >
            <span className="w-3 text-text-muted">{open ? '▾' : '▸'}</span>
            {/* A4: die E2E-ID steht VOR dem Kettennamen (`ETO-E2E-001 Order to Cash`). */}
            {opts.humanId && <IdLabel id={opts.humanId} />}
            <span>{opts.name}</span>
            <span className="font-normal text-text-muted">
              — {t('plans.testCaseCount', { count: ids.length })} ·{' '}
              {t('plans.group.inScope', { count: inScopeCount })}
            </span>
          </button>
        </td>
      </tr>
    );
  };

  // Ein Teilbaum: Teilprojekt/Stream → Prozess/Kette → Testfälle. Gruppen ohne eine einzige
  // sichtbare Zeile fallen komplett weg — eine leere Gruppenüberschrift wäre irreführend (#84).
  const renderTopGroup = (g: TopGroup) => {
    const children = g.children
      .map((leaf) => ({ leaf, visible: leaf.rows.filter(rowVisible) }))
      .filter((c) => c.visible.length > 0);
    if (children.length === 0) return null;
    return (
      <Fragment key={g.key}>
        {renderGroupHeader({
          groupKey: g.key,
          name: g.name,
          humanId: null,
          level: 0,
          rows: g.children.flatMap((c) => c.rows),
          visibleRows: children.flatMap((c) => c.visible),
        })}
        {isOpen(g.key, true) &&
          children.map((c) => (
            <Fragment key={c.leaf.key}>
              {renderGroupHeader({
                groupKey: c.leaf.key,
                name: c.leaf.name,
                humanId: c.leaf.humanId,
                level: 1,
                rows: c.leaf.rows,
                visibleRows: c.visible,
              })}
              {isOpen(c.leaf.key, true) && c.visible.map((r) => renderRow(r, c.leaf.key))}
            </Fragment>
          ))}
      </Fragment>
    );
  };

  // A4: die drei Reiter. Der dritte trägt seine Stückzahl im Namen („Nicht zugeordnet · 12") —
  // sie ist die Antwort auf „habe ich etwas übersehen?" und soll nicht erst nach dem Klick da
  // sein.
  const tabs: { key: ScopeView; label: string }[] = [
    { key: 'teilprojekt', label: t('plans.view.teilprojekt') },
    { key: 'e2e', label: t('plans.view.e2e') },
    { key: 'unassigned', label: `${t('plans.view.unassigned')} · ${unassignedRows.length}` },
  ];

  return (
    <div className="rounded-card border border-[var(--border)] bg-bg-surface">
      <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
        <span className="flex items-baseline gap-3">
          <span className="text-sm font-semibold text-text-primary">{t('plans.scopeCol')}</span>
          {/* A4: EINDEUTIGE Testfälle — derselbe Testfall steht in beiden Sichten, ist aber EIN
              Testfall. Ohne diese Zahl liest sich die Doppelanzeige wie doppelte Zählung. */}
          <span className="text-[11px] text-text-muted">
            {t('plans.uniqueInScope', { count: uniqueInScopeCount })}
          </span>
        </span>
        {/* #114 (A9): „Alle übernehmen" ist eine Massen-Zuweisung — am abgeschlossenen Plan
            antwortet der Server mit 409, also gar nicht erst anbieten. */}
        {canManage && !readOnly && suggestionCount > 0 && (
          <button
            type="button"
            onClick={() => applyAll.mutate()}
            disabled={applyAll.isPending}
            className="rounded-btn border border-dashed border-[var(--border)] bg-[var(--tint-blue)] px-2 py-1 text-xs text-text-body disabled:opacity-60"
          >
            ✦ {t('plans.applyAll', { count: suggestionCount })}
          </button>
        )}
      </div>
      {/* Übersprungene Vorschläge (Platzhalter ohne Projektrolle, deaktivierte Nutzer): aufklappbare
          Liste mit Testfall-ID · Titel in UI-Sprache · Tester · Grund. <output> (implizit role="status") meldet
          die Zusammenfassung der Sprachausgabe, ohne den Fokus zu stehlen. */}
      {applyResult && applyResult.skipped.length > 0 && (
        <output className="flex items-start justify-between gap-3 border-b border-[var(--border)] bg-[var(--tint-yellow)] px-4 py-2 text-xs text-text-body">
          <details className="min-w-0 flex-1">
            <summary className="cursor-pointer">
              {t('plans.applyResult.skipped', { count: applyResult.skipped.length })}
            </summary>
            <ul className="mt-1 max-h-48 list-none space-y-0.5 overflow-y-auto pl-0">
              {applyResult.skipped.map((s) => (
                <li key={s.test_case_id} className="flex flex-wrap gap-x-2">
                  <span className="font-mono">{s.human_id}</span>
                  <span>{pickLang(i18n.resolvedLanguage, s.title_de, s.title_en)}</span>
                  <span className="text-text-muted">{s.tester_name}</span>
                  <span className="text-text-muted">
                    {t(`plans.applyResult.reason.${s.reason}`)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
          <button
            type="button"
            onClick={() => setApplyResult(null)}
            className="shrink-0 text-text-muted hover:text-text-body"
          >
            {t('plans.applyResult.dismiss')}
          </button>
        </output>
      )}
      {/* #114 (A9): EIN ruhiger Hinweis für die ganze Tabelle statt eines Titels an jedem
          gesperrten Bedienelement — 205 Checkboxen mit derselben Begründung wären Lärm. Er nennt
          nur den Grund „Plan abgeschlossen"; das fehlende Recht (`canManage`) ist ein anderer
          Grund und hat seine eigene, seit #58 bestehende Darstellung (Tester als Text, keine
          Auswahl-Spalte). */}
      {readOnly && (
        <p className="border-b border-[var(--border)] px-4 py-2 text-[11px] text-text-muted">
          {t('plans.closedReadOnly')}
        </p>
      )}
      {/* A4: Reiter statt Abschnitte untereinander — Abschnitte hat der Nutzer ausdrücklich
          abgelehnt („dann muss ich wieder ewig scrollen"). */}
      <div
        role="tablist"
        aria-label={t('plans.view.ariaLabel')}
        className="flex flex-wrap gap-1 border-b border-[var(--border)] px-4 py-2"
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={view === tab.key}
            onClick={() => setViewParam(tab.key)}
            className={`rounded-btn px-3 py-1 text-xs ${
              view === tab.key
                ? 'bg-[var(--tint-blue)] font-semibold text-text-primary'
                : 'text-text-body hover:bg-[var(--bg-subtle)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* #84: Suche + Filter gelten für ALLE Rollen (Lese-Operationen) — nur die Checkbox selbst
          bleibt an plan.manage gebunden (s. o.). */}
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--border)] px-4 py-3">
        <input
          type="search"
          value={qParam}
          onChange={(e) => setQParam(e.target.value)}
          placeholder={t('plans.search.placeholder')}
          aria-label={t('plans.search.ariaLabel')}
          className="w-56 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-body"
        />
        <label className="flex items-center gap-1.5 text-xs text-text-body">
          <input
            type="checkbox"
            checked={onlyScope}
            onChange={(e) => setOnlyScopeParam(e.target.checked ? 'true' : 'false')}
          />
          {t('plans.filter.onlyInScope')}
        </label>
        <label className="flex items-center gap-1.5 text-xs text-text-body">
          <input
            type="checkbox"
            checked={onlyUnassigned}
            onChange={(e) => setOnlyUnassignedParam(e.target.checked ? 'true' : 'false')}
          />
          {t('plans.filter.onlyUnassigned')}
        </label>
        {/* #78: keine stille Kappung — solange Suche/Filter aktiv sind UND dadurch Zeilen
            wegfallen, steht die Zahl direkt daneben. */}
        {filterActive && hiddenRowCount > 0 && (
          <span className="text-[11px] text-text-muted">
            {t('plans.filter.hiddenCount', { count: hiddenRowCount })}
          </span>
        )}
      </div>
      {/* I3: die Leermeldung steht NEBEN der Tabelle, nie an ihrer Stelle (Muster aus den
          Katalog-Tabs) — die Tabelle bleibt gemountet, auch wenn ihr Body gerade leer ist. */}
      {searching && visibleRowCount === 0 && (
        <div className="border-b border-[var(--border)] px-4 py-3">
          <SearchEmptyNotice query={trimmedQuery} />
        </div>
      )}
      {/* Ein Reiter kann auch ohne Suche leer sein (kein Stream angelegt, nichts Unzugeordnetes).
          Eine stumme leere Tabelle wäre dort die schlechtere Antwort. */}
      {!searching && visibleRowCount === 0 && (
        <div className="border-b border-[var(--border)] px-4 py-3 text-xs text-text-muted">
          {t('plans.view.empty')}
        </div>
      )}
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-text-muted">
            {/* #91: Kopf-Checkbox markiert/entmarkiert nur die SICHTBARE (gefilterte) Menge des
                aktiven Reiters — sonst würde "alle markieren" auch Zeilen treffen, die Suche oder
                Reiter gerade ausblenden. Nur bei plan.manage (#58). */}
            {canManage && (
              <th className="px-4 py-2">
                <TriCheckbox
                  label={t('plans.bulk.selectAllVisible')}
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected}
                  onChange={() => selection.setMany(visibleIds, !allVisibleSelected)}
                />
              </th>
            )}
            <th className="px-4 py-2">{t('plans.scopeCol')}</th>
            <th className="px-4 py-2">{t('plans.name')}</th>
            <th className="px-4 py-2">{t('plans.tester')}</th>
            <th className="px-4 py-2">{t('plans.progress')}</th>
          </tr>
        </thead>
        <tbody>
          {/* „Nicht zugeordnet" ist eine flache Liste: es gibt per Definition keine Gruppe, unter
              die diese Testfälle gehören würden. */}
          {view === 'unassigned'
            ? unassignedRows.filter(rowVisible).map((r) => renderRow(r, 'unassigned'))
            : activeGroups.map(renderTopGroup)}
        </tbody>
      </table>
      {/* #91: NUR bei plan.manage — ohne Schreibrecht gibt es weder Checkboxen noch etwas zum
          Sammeln. Direkt nach der Tabelle, sticky am unteren Rand (Zielschirm 1280×660: ohne
          Scrollen erreichbar, während man oben markiert). */}
      {canManage && (
        <BulkActionBar
          count={selection.selected.size}
          members={members ?? []}
          // #114 (A9): Markieren bleibt möglich (Lese-Operation), die Sammelaktionen selbst nicht
          // — sonst wäre die Leiste eine Reihe von Knöpfen, die alle in 409 laufen.
          readOnly={readOnly}
          pending={bulkAssign.isPending || setScope.isPending}
          onAssign={(testerId) =>
            bulkAssign.mutate({ test_case_ids: [...selection.selected], tester_id: testerId })
          }
          onRemoveTester={() =>
            bulkAssign.mutate({ test_case_ids: [...selection.selected], tester_id: null })
          }
          onSetScope={bulkScope}
          onClear={selection.clear}
        />
      )}
      <div className="border-t border-[var(--border)] px-4 py-3 text-[11px] text-text-muted">
        {t('plans.assignProgress', {
          done: items.filter((i) => i.in_scope && i.assigned_tester_id).length,
          total: items.filter((i) => i.in_scope).length,
        })}
      </div>
      {openRun && (
        <SidePanel
          title={`${t('plans.runHistory')} — ${openRun.title}`}
          onClose={() => setOpenRun(null)}
        >
          <RunHistory projectId={projectId} planId={planId} testCaseId={openRun.id} />
        </SidePanel>
      )}
    </div>
  );
}
